import React, { useContext } from "react";
import { useRouteMatch } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import firebase from "../../firebase";
import FIREBASE from "firebase";
import { AuthContext } from "../../auth";
import { SnackbarAlert } from "../SnackbarAlert";
import DoctorBadge from "../DoctorBadge";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

const useStyles = makeStyles((theme) => ({
  input: {
    margin: 8,
  },
  button: {
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
}));

const PostForm = () => {
  const classes = useStyles();
  const { currentUser } = useContext(AuthContext);
  const { setSnackbarState } = useContext(SnackbarAlert);
  const match = useRouteMatch();
  const { path, url, params } = match;

  const getInitials = (name) => {
    let parts = name.split(" ");
    let initials = "";
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].length > 0 && parts[i] !== "") {
        initials += parts[i][0];
      }
    }
    return initials;
  };

  return (
    <Formik
      initialValues={{
        inputTitle: "",
        inputMessage: "",
        hideName: false,
      }}
      validationSchema={Yup.object().shape({
        inputTitle: Yup.string().required("Please enter your query"),
        inputMessage: Yup.string().required("Please explain your query"),
        hideName: Yup.boolean(),
      })}
      onSubmit={async (values, actions) => {
        try {
          let displayName = "";
          if (values.hideName === true) {
            displayName = "Anonymous User";
          } else {
            displayName = currentUser.displayName;
          }
          const postData = {
            fromPost: false,
            message: values.inputMessage,
            postsDate: FIREBASE.database.ServerValue.TIMESTAMP,
            postsEdited: false,
            postsName: displayName,
            postsTitle: values.inputTitle,
            postsUserID: currentUser.uid,
            votes: 0,
          };
          const newPostKey = await firebase
            .database()
            .ref(`posts/${params.id}`)
            .push().key;
          let updates = {};
          updates[`posts/${params.id}/${newPostKey}`] = postData;
          await firebase.database().ref().update(updates);
          actions.resetForm();
          setSnackbarState({
            state: true,
            message: "Successfully posted! 👍",
            status: "info",
          });
        } catch (error) {
          actions.resetForm();
          //console.log(error);
          setSnackbarState({
            state: true,
            message: "Something went wrong. 😟",
            status: "error",
          });
        }
      }}
    >
      {(formik) => (
        <Grid
          container
          direction="column"
          justify="flex-start"
          alignItems="stretch"
        >
          <form
            onSubmit={(e) => {
              //e.preventDefault();
              formik.handleSubmit();
            }}
            noValidate
          >
            <Grid item xs={12} zeroMinWidth>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar alt={currentUser.displayName}>
                      {getInitials(currentUser.displayName)}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography component="div">
                        <Box
                          fontSize={28}
                          fontWeight="fontWeightBold"
                          className={classes.secondaryText}
                        >
                          {currentUser.displayName}
                          <DoctorBadge type="BIG" id={currentUser.uid} />
                        </Box>
                      </Typography>
                    }
                  />
                  <p style={{ fontSize: "20px" }}>
                    You are posting in the <u>{params.id}</u> group
                  </p>
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} zeroMinWidth>
              <TextField
                id="inputTitle"
                name="inputTitle"
                placeholder="Ask your questions or tell us your recommendations, queries and concerns."
                margin="normal"
                variant="outlined"
                className={classes.input}
                disabled={formik.isSubmitting}
                InputLabelProps={{
                  shrink: true,
                }}
                {...formik.getFieldProps("inputTitle")}
                error={formik.errors.inputTitle && formik.touched.inputTitle}
                helperText={
                  formik.errors.inputTitle && formik.touched.inputTitle
                    ? formik.errors.inputTitle
                    : "Title"
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} zeroMinWidth>
              <TextField
                id="inputMessage"
                name="inputMessage"
                placeholder="Explain your recommendation, query, or concern."
                margin="normal"
                variant="outlined"
                className={classes.input}
                disabled={formik.isSubmitting}
                InputLabelProps={{
                  shrink: true,
                }}
                {...formik.getFieldProps("inputMessage")}
                error={
                  formik.errors.inputMessage && formik.touched.inputMessage
                }
                helperText={
                  formik.errors.inputMessage && formik.touched.inputMessage
                    ? formik.errors.inputMessage
                    : "Message"
                }
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
            <Grid item xs={12} zeroMinWidth>
              <Box component="div" display="inline" justify="flex-start" m={1}>
                <Button
                  variant="contained"
                  className={classes.button}
                  disabled={formik.isSubmitting}
                  type="submit"
                  disableElevation
                >
                  Post
                </Button>
              </Box>
              <Box component="div" display="inline" justify="flex-end" m={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="hideName"
                      name="hideName"
                      type="checkbox"
                      checked={formik.values.hideName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={formik.isSubmitting}
                      color="secondary"
                    />
                  }
                  label="Post as Anonymous User"
                />
              </Box>
            </Grid>
          </form>
        </Grid>
      )}
    </Formik>
  );
};

export default PostForm;
